import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UtilityService } from '@nttp/common-service/utility-service';
import { RegisterInforService } from '@pg-app/auth/services/register-infor.service';
import { RegisterStep1Service } from '@pg-app/auth/services/register-step-1.service';
import { environment } from '@pg-env/environment';
import { Subject, ReplaySubject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { ShareService } from '@nttp/common-service/share-service';
import { LeadType, LeadTypeString } from '@nttp/enum/lead-type.enum';
import { RecaptchaComponent } from 'ng-recaptcha';
import { AccountTypeHelper } from '@pg-app/shared/common/account-type-helper';
import { Regex } from '@pg-app/shared/constants/regex';
import { CRMAccountType } from '@nttp/enum/account-type.enum';

function compareEmail(c: AbstractControl) {
  const v = c.value;
  return v.email.toLowerCase() === v.confirmEmail.toLowerCase()
    ? null
    : {
        emailnotmatch: true
      };
}

function comparePin(c: AbstractControl) {
  const v = c.value;
  return v.pinCode === v.confirmPin
    ? null
    : {
        pinnotmatch: true
      };
}

function comparePass(c: AbstractControl) {
  const v = c.value;
  return v.password === v.confirmPass
    ? null
    : {
        passnotmatch: true
      };
}

export interface Country {
  id: string;
  countryCode: string;
  code: string;
}

@Component({
  selector: 'np-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  private recaptchaComponent: RecaptchaComponent;
 @ViewChild('captchaRef') set content(content: RecaptchaComponent) {
    if(content) { // initially setter gets called with undefined
        this.recaptchaComponent = content;
    }
 }

  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  domain = environment.domain;
  brandName = environment.brandName;

  individualForm: FormGroup;
  registerForm: FormGroup;

  customerType = '';
  wrongPin = false;
  destroy$ = new Subject<boolean>();
  passwordPattern = '^(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[(!@#$%^&*()_+|~ ={}[:”;<>?,., )]).{8,}$';
  // user name can contains characters (a-z, A-Z, 0-9, dash -,  apostrophe ', space), no special characters
  namePattern = `([A-Za-z0-9\-' ]+)`;
  countries = [];
  titles = [];

  acceptPolicy = false;
  acceptMarketingEmails = false;
  submitForm = false;

  emailExist = false;
  emailValid = false;
  leadTypeString = LeadTypeString;
  leadTypeEnum = LeadType;
  checked = this.isPrivalgoMarkets ? LeadTypeString.MarketsCorporate : LeadTypeString.Private;
  readonlyEmail = true;
  emailUrl: string;
  partnerResidencies = [];
  isVATRegistered = false;
  customerTypeNumber = null;

  callingCodes: any;
  filteredCallingCodes: ReplaySubject<Country[]> = new ReplaySubject<Country[]>(1);

  accountNumber: string;
  recaptchaV2SiteKey = environment.recaptchaV2SiteKey;
  recaptchaToken = '';

  commonMessage = CommonMessages;

  get titleICtrl(): FormControl {
    return this.individualForm.get('title') as FormControl;
  }
  get firstNameICtrl(): FormControl {
    return this.individualForm.get('firstName') as FormControl;
  }
  get middleNameICtrl(): FormControl {
    return this.individualForm.get('middleName') as FormControl;
  }
  get lastNameICtrl(): FormControl {
    return this.individualForm.get('lastName') as FormControl;
  }
  get emailICtrl(): FormControl {
    return this.individualForm.get('email') as FormControl;
  }
  get confirmEmailICtrl(): FormControl {
    return this.individualForm.get('confirmEmail') as FormControl;
  }
  get pinCodeICtrl(): FormControl {
    return this.individualForm.get('pinCode') as FormControl;
  }
  get confirmPinICtrl(): FormControl {
    return this.individualForm.get('confirmPin') as FormControl;
  }
  get passwordICtrl(): FormControl {
    return this.individualForm.get('password') as FormControl;
  }
  get confirmPassICtrl(): FormControl {
    return this.individualForm.get('confirmPass') as FormControl;
  }

  get companyRegNoPCtrl(): FormControl {
    return this.individualForm.get('companyRegNo') as FormControl;
  }
  get businessNamePCtrl(): FormControl {
    return this.individualForm.get('businessName') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private registerStep1Service: RegisterStep1Service,
    private utilityService: UtilityService,
    private router: Router,
    private registerInfor: RegisterInforService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private shareService: ShareService,
  ) {
    this.customerType = this.route.snapshot.queryParams['customertype'];
    this.emailUrl = this.route.snapshot.queryParams['email'];
    this.accountNumber = this.route.snapshot.queryParams['utm_source'];

    if (this.customerType) {
      this.customerTypeNumber = AccountTypeHelper.getLeadType(this.customerType);
    }
    this.readonlyEmail = !this.customerTypeNumber;
  }

  ngOnInit() {
    this.registerForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.individualForm = this.fb.group(
      {
        title: ['', Validators.required],
        firstName: ['', [Validators.required, Validators.pattern(Regex.safeCharacters), Validators.maxLength(255)]],
        middleName: ['', [Validators.pattern(Regex.safeCharacters), Validators.maxLength(255)]],
        lastName: ['', [Validators.required, Validators.pattern(Regex.safeCharacters), Validators.maxLength(255)]],
        email: ['', [Validators.required, Validators.email]],
        country: ['', Validators.required],
        companyRegNo: [''],
        companyRegNoCor: [''],
        businessName: ['', Validators.maxLength(100)],
        businessNameCor: ['', [Validators.required, Validators.maxLength(100)]],
        contactNumber: ['', Validators.required],
        confirmEmail: '',
        pinCode: ['', [Validators.required, Validators.minLength(6)]],
        confirmPin: '',
        password: ['', [Validators.required, Validators.pattern(this.passwordPattern)]],
        confirmPass: '',
        partnerResidency: '',
        vatNumber: '',
        vatCountry: '',
        callingCode: ['', Validators.required],
        callingCodeFilter: '',
        recaptchaReactive: [null, Validators.required]
      },
      {
        validator: [comparePin, comparePass, compareEmail]
      }
    );

    if (this.emailUrl && !this.customerTypeNumber) {
      this.registerForm.get('email').setValue(this.emailUrl);
    }

    if (this.emailUrl && this.customerTypeNumber) {
      this.individualForm.get('email').setValue(this.emailUrl);
      this.individualForm.get('confirmEmail').setValue(this.emailUrl);
    }

    this.pinCodeICtrl.valueChanges
      .pipe(
        debounceTime(450),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.checkPin(value);
      });

    this.getTitles();
    this.getCountries();
    this.getPartnerResidency();
    this.getCallingCode();
  }

  individualSubmit() {
    this.submitForm = true;
    this.individualForm.markAllAsTouched();
    if (this.individualForm.valid && !this.wrongPin && this.acceptPolicy) {
      this.verifyEmailAndCaptchaAndPhone();
    }
  }

  private verifyEmailAndCaptchaAndPhone() {
      // Check email existence
      const param = {
          email: this.emailICtrl.value
        };
      const checkEmailExistence = this.registerStep1Service.checkEmailExist(param);

      // Check email validation
      const checkEmailValidation = this.registerStep1Service.isEmailValid(param);

      // check phone number
      const params = {
        callingCodeId: this.individualForm.get('callingCode').value.id,
        phoneNumber: this.individualForm.get('contactNumber').value
      };
      const checkPhoneApi = this.utilityService.combinePhonenumber(params);

      forkJoin([checkEmailExistence, checkEmailValidation, checkPhoneApi]).subscribe(res => {
        // check email existence
        this.emailExist = res[0].Exists;
        if (this.emailExist) {
          this.toastr.error(CommonMessages.emailExist);
          return;
        }

        // check email validation
        this.emailValid = res[1];
        if (!this.emailValid) {
          this.toastr.error(CommonMessages.emailInvalid);
          return;
        }

        const combinedPhoneDto = res[2];
        if (combinedPhoneDto.error) {
          this.toastr.error(combinedPhoneDto.error);
          return;
        }
        this.registerSubmit(combinedPhoneDto.phoneNumber);
      },
      error => {
        this.recaptchaComponent.reset();
      });
  }

  isCorporate(leadType: LeadType) {
    const result = (leadType === LeadType.Corporate
      || leadType === LeadType.MarketsCorporate
      || leadType === LeadType.MarketsInstitution);
    return result;
  }

  registerSubmit(phoneNumber) {
    if (!this.recaptchaToken) {
      return;
    }

    const title = this.titleICtrl.value;
    const countrySelected = this.countries.find(i => i.id === this.individualForm.get('country').value);
    const individualAccount = {
      title: title
        ? {
            titleId: title.id,
            title: title.name
          }
        : null,
      titleId: this.titleICtrl.value.id,
      firstName: this.upperCaseName(this.firstNameICtrl.value),
      middleName: this.middleNameICtrl.value ? this.upperCaseName(this.middleNameICtrl.value) : null,
      lastName: this.upperCaseName(this.lastNameICtrl.value),
      email: this.emailICtrl.value,
      confirmEmail: this.confirmEmailICtrl.value,
      organization:
        this.isCorporate(this.customerTypeNumber)
          ? {
              companyRegNo: this.individualForm.get('companyRegNoCor').value
                ? this.individualForm.get('companyRegNoCor').value
                : null,
              businessName: this.individualForm.get('businessNameCor').value
                ? this.individualForm.get('businessNameCor').value
                : null,
              businessType: null
            }
          : {
              companyRegNo: this.individualForm.get('companyRegNo').value
                ? this.individualForm.get('companyRegNo').value
                : null,
              businessName: this.individualForm.get('businessName').value
                ? this.individualForm.get('businessName').value
                : null,
              businessType: null
            },
      pinCode: this.isCorporate(this.customerTypeNumber) ? null : this.pinCodeICtrl.value,
      password: this.isCorporate(this.customerTypeNumber) ? null : this.passwordICtrl.value,
      clientId: environment.clientId,
      customerType: this.customerType,
      accountType: CRMAccountType.Lead,
      portalType: 1,
      token: this.route.snapshot.queryParams['token'],
      country: countrySelected,
      phoneNumber: phoneNumber,
      callingCode: this.individualForm.get('callingCode').value.code,
      partnerAccountNumber: this.accountNumber ? this.accountNumber : null,
      doNotBulkEmail : !this.acceptMarketingEmails,
      partnerResidency: this.individualForm.get('partnerResidency').value
        ? this.individualForm.get('partnerResidency').value
        : null,
      isVATRegistered: this.isVATRegistered,
      vATNumber: this.isVATRegistered ? this.individualForm.get('vatNumber').value : null,
      vATCountryCode: this.isVATRegistered ? this.individualForm.get('vatCountry').value : null,
      recaptchaToken: this.recaptchaToken
    };
    
    this.registerStep1Service.submitIndividualForm(individualAccount).subscribe(
      res => {
        this.registerInfor.email = this.emailICtrl.value;
        this.registerInfor.userId = res;
        this.registerInfor.customerType = this.customerType;
        if (!this.isCorporate(this.customerTypeNumber)) {
          this.router.navigate(['/auth/register/verify-email']);
        } else {
          this.dialog.open(CommonDialogComponent, {
            data: {
              title: 'A member of the team will contact you shortly <br> to continue the account opening process.',
              dialogType: 1,
              routingUrl: 'auth/login/'
            }
          });
        }
      },
      err => {
        this.recaptchaComponent.reset();
      }
    );
  }

  checkPin(pin) {
    if (!pin) {
      return;
    }
    for (let i = 0; i < pin.length - 2; i++) {
      if (
        (pin.charCodeAt(i) + 1 === pin.charCodeAt(i + 1) && pin.charCodeAt(i) + 2 === pin.charCodeAt(i + 2)) ||
        (pin.charCodeAt(i) === pin.charCodeAt(i + 1) && pin.charCodeAt(i) === pin.charCodeAt(i + 2)) ||
        (pin.charCodeAt(i) - 1 === pin.charCodeAt(i + 1) && pin.charCodeAt(i) - 2 === pin.charCodeAt(i + 2))
      ) {
        this.wrongPin = true;
        return;
      }
    }
    this.wrongPin = false;
  }

  getCountries() {
    this.shareService.countryList$.subscribe(data => {
      this.countries = data || [];
    });
  }

  private getTitles() {
    this.shareService.titleList$.subscribe(data => {
      this.titles = data || [];
      const defaultTitle = this.titles.find(item => item.name === 'Mr');
      this.titleICtrl.setValue(defaultTitle);
    });
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  back() {
    this.recaptchaComponent.reset();
    this.recaptchaToken = '';
    this.customerTypeNumber = null;
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  upperCaseName(value) {
    if (!value) {
      return null;
    }
    const valueSplit = value.split(' ').filter(item => item !== '');
    const name = valueSplit.map(item => {
      return this.capitalizeFirstLetter(item);
    });
    return name.join(' ');
  }

  changeType(event) {
    this.checked = event.value;
  }

  createAccount() {
    if (this.registerForm.valid) {
      const param = {
        email: this.registerForm.get('email').value
      };

      this.registerStep1Service.checkEmailExist(param).subscribe(result => {
        this.emailExist = result.Exists;
        if (result.Exists) {
          const notiParam = {
            email: this.registerForm.get('email').value,
            accountType: AccountTypeHelper.getLeadType(this.checked)
          }

          this.registerStep1Service.sendExistingAccountRegistrationNotification(notiParam).subscribe(() => {
            this.dialog.open(CommonDialogComponent, {
              maxWidth: '42vw',
              data: {
                title: `Thank you for registering with ${this.brandName}.  Your account manager will contact you shortly to complete your registration.`,
                dialogType: 1
              }
            });
          });
          return;
        }

        this.registerStep1Service.isEmailValid(param).subscribe(isEmailValid => {
          this.emailValid = isEmailValid;
          if (!isEmailValid) {
            this.toastr.error(CommonMessages.emailInvalid);
            return;
          }

          this.customerType = this.checked;
          this.customerTypeNumber = AccountTypeHelper.getLeadType(this.customerType);
          this.individualForm.get('email').setValue(this.registerForm.get('email').value);
          this.individualForm.get('confirmEmail').setValue(this.registerForm.get('email').value);

          if (this.isCorporate(this.customerTypeNumber)) {
            this.individualForm.get('pinCode').setValidators([]);
            this.individualForm.get('password').setValidators([]);
            this.individualForm.get('businessNameCor').setValidators([Validators.required, Validators.maxLength(100)]);
          } else {
            this.individualForm.get('pinCode').setValidators([Validators.required, Validators.minLength(6)]);
            this.individualForm
              .get('password')
              .setValidators([Validators.required, Validators.pattern(this.passwordPattern)]);
            this.individualForm.get('businessNameCor').setValidators([]);
          }

          if (this.customerTypeNumber === LeadType.Affiliate || this.customerTypeNumber === LeadType.MarketsPartner) {
            this.individualForm.get('businessName').setValidators([Validators.maxLength(100)]);
          } else {
            this.individualForm.get('businessName').setValidators([]);
          }
          this.individualForm.get('pinCode').updateValueAndValidity();
          this.individualForm.get('password').updateValueAndValidity();
          this.individualForm.get('businessNameCor').updateValueAndValidity();
          this.individualForm.get('businessName').updateValueAndValidity();

        });

      });

    }
  }

  getPartnerResidency() {
    this.registerStep1Service.getPartnerResidency().subscribe(res => {
      this.partnerResidencies = res;
    });
  }

  private filterCountries() {
    if (!this.countries) {
      return;
    }
    // get the search keyword
    let search = this.individualForm.get('callingCodeFilter').value;
    if (!search) {
      this.filteredCallingCodes.next(this.callingCodes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the callingCodes
    this.filteredCallingCodes.next(
      this.callingCodes.filter(
        item =>
          item.code.toLowerCase().indexOf(search) > -1 ||
          item.countryName.toLowerCase().indexOf(search.toLowerCase()) > -1
      )
    );
  }

  private getCallingCode() {
    this.shareService.callingCodeList$.subscribe(data => {
      this.callingCodes = data || [];
      this.filteredCallingCodes.next(this.callingCodes.slice());
      this.individualForm
        .get('callingCodeFilter')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.filterCountries();
        });
    });
  }

  resolvedRecaptchaChallenge(captchaResponse: string) {
    this.recaptchaToken = captchaResponse;
  }
}
